import { AlertTriangle, Mail, ShieldCheck, Clock, Database, ExternalLink } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function DeleteAccountPage() {
  return (
    <div className="min-h-screen bg-app">
      {/* Header */}
      <header className="border-b border-subtle px-5 md:px-10 py-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <a href="/" className="flex items-center gap-3">
            <BrandLogo size={36} />
            <div className="font-display font-medium text-primary-fg">Family Brain</div>
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16">
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="w-6 h-6 text-rose-warm" strokeWidth={1.6} />
          <p className="text-xs uppercase tracking-widest text-rose-warm font-semibold">Cancellazione account</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">
          Eliminare il tuo account
        </h1>
        <p className="text-base text-secondary-fg leading-relaxed mb-10">
          Questa pagina spiega come richiedere la cancellazione definitiva del tuo account Family Brain
          e dei dati associati, in conformità con i requisiti di Google Play e con il GDPR.
        </p>

        {/* Modo 1: dall'app */}
        <section data-testid="delete-account-in-app" className="bg-surface border border-subtle rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-sage" strokeWidth={1.6} />
            Se hai ancora l&apos;app installata
          </h2>
          <ol className="space-y-3 text-sm text-secondary-fg leading-relaxed list-decimal pl-5">
            <li>Apri Family Brain sul tuo dispositivo.</li>
            <li>Vai in <strong className="text-primary-fg">Impostazioni</strong> (icona ingranaggio).</li>
            <li>Scorri fino in fondo alla sezione <strong className="text-primary-fg">Zona pericolosa</strong>.</li>
            <li>Premi <strong className="text-primary-fg">&ldquo;Elimina account&rdquo;</strong> e conferma con la tua password.</li>
          </ol>
          <a
            href="/settings"
            data-testid="delete-account-open-settings"
            className="btn-primary mt-6 inline-flex items-center gap-2 !text-sm"
          >
            Vai a Impostazioni <ExternalLink className="w-4 h-4" />
          </a>
        </section>

        {/* Modo 2: senza app */}
        <section data-testid="delete-account-by-email" className="bg-surface border border-subtle rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-brand" strokeWidth={1.6} />
            Se hai già disinstallato l&apos;app
          </h2>
          <p className="text-sm text-secondary-fg leading-relaxed mb-4">
            Puoi comunque richiedere la cancellazione scrivendo a:
          </p>
          <a
            href="mailto:info@familybrain.it?subject=Richiesta%20cancellazione%20account&body=Chiedo%20la%20cancellazione%20definitiva%20del%20mio%20account%20Family%20Brain.%0A%0AEmail%20associata%20all%27account%3A%20%0A"
            data-testid="delete-account-email-link"
            className="inline-flex items-center gap-2 text-brand font-medium hover:underline"
          >
            info@familybrain.it
            <ExternalLink className="w-4 h-4" />
          </a>
          <p className="text-sm text-secondary-fg leading-relaxed mt-4">
            Nell&apos;email includi l&apos;indirizzo email associato all&apos;account. Ti risponderemo entro <strong className="text-primary-fg">14 giorni lavorativi</strong> ed
            elimineremo l&apos;account entro <strong className="text-primary-fg">30 giorni</strong> dalla verifica dell&apos;identità.
          </p>
        </section>

        {/* Cosa viene eliminato */}
        <section className="bg-surface border border-subtle rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-rose-warm" strokeWidth={1.6} />
            Dati eliminati definitivamente
          </h2>
          <ul className="space-y-2 text-sm text-secondary-fg leading-relaxed list-disc pl-5">
            <li>Il tuo profilo utente (nome, email, password, avatar)</li>
            <li>Tutti i tuoi messaggi personali con l&apos;assistente Memo AI</li>
            <li>Le tue sottoscrizioni alle notifiche push su tutti i dispositivi</li>
            <li>I documenti, foto, ricevute, referti da te caricati nel Vault</li>
            <li>Gli eventi da te creati nel calendario familiare</li>
            <li>Gli articoli da te aggiunti alla lista della spesa</li>
            <li>Le attività (task) da te create</li>
            <li>I tuoi messaggi nella chat di famiglia</li>
            <li>I suggerimenti automatici di Memo AI legati ai tuoi messaggi</li>
            <li>I riferimenti nel programma referral (chi ti ha invitato, chi hai invitato)</li>
          </ul>

          <div className="mt-6 pt-6 border-t border-subtle">
            <h3 className="font-display text-base font-medium mb-3">Se sei l&apos;unico membro della tua famiglia</h3>
            <p className="text-sm text-secondary-fg leading-relaxed">
              Verranno eliminati anche <strong className="text-primary-fg">la famiglia</strong> stessa e tutti i suoi dati condivisi
              (documenti, eventi, chat, spesa, task): non resta nulla.
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-subtle">
            <h3 className="font-display text-base font-medium mb-3">Se sei il proprietario e ci sono altri membri</h3>
            <p className="text-sm text-secondary-fg leading-relaxed">
              La proprietà della famiglia viene <strong className="text-primary-fg">trasferita automaticamente</strong> al membro più anziano
              (per data d&apos;iscrizione). Solo i tuoi dati personali e i contenuti che hai creato vengono rimossi.
              I dati degli altri membri restano intatti.
            </p>
          </div>
        </section>

        {/* Cosa viene conservato */}
        <section className="bg-surface border border-subtle rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-warm" strokeWidth={1.6} />
            Dati conservati (e per quanto)
          </h2>
          <ul className="space-y-3 text-sm text-secondary-fg leading-relaxed">
            <li>
              <strong className="text-primary-fg">Ricevute di pagamento (Stripe/altri)</strong> — Conservate <strong className="text-primary-fg">10 anni</strong> per obbligo di legge fiscale italiano/UE.
              Nel nostro database gli identificativi personali vengono <strong className="text-primary-fg">anonimizzati</strong> subito dopo la richiesta.
            </li>
            <li>
              <strong className="text-primary-fg">Contenuti creati dagli altri membri della famiglia</strong> — Non ti appartengono, non vengono eliminati.
              Se il tuo nome appare nei loro messaggi storici come mittente citato, resta come riferimento immutabile.
            </li>
            <li>
              <strong className="text-primary-fg">Backup crittografati del database</strong> — Conservati fino a <strong className="text-primary-fg">30 giorni</strong> dal provider cloud MongoDB Atlas.
              Trascorso quel periodo, i tuoi dati vengono cancellati anche dai backup.
            </li>
            <li>
              <strong className="text-primary-fg">Log tecnici anonimizzati</strong> — Log di sicurezza (IP, timestamp) conservati fino a <strong className="text-primary-fg">180 giorni</strong>,
              senza collegamento diretto al tuo account eliminato.
            </li>
          </ul>
        </section>

        {/* Tempi */}
        <section className="bg-surface border border-subtle rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-sage" strokeWidth={1.6} />
            Tempi di elaborazione
          </h2>
          <ul className="space-y-2 text-sm text-secondary-fg leading-relaxed list-disc pl-5">
            <li><strong className="text-primary-fg">Dall&apos;app</strong>: cancellazione <strong className="text-primary-fg">istantanea</strong>. Al termine vieni disconnesso e non potrai più accedere.</li>
            <li><strong className="text-primary-fg">Via email</strong>: elaborata entro <strong className="text-primary-fg">14 giorni lavorativi</strong>, esecuzione entro <strong className="text-primary-fg">30 giorni</strong>.</li>
            <li><strong className="text-primary-fg">Backup</strong>: rimossi entro <strong className="text-primary-fg">30 giorni</strong> per rotazione automatica.</li>
          </ul>
        </section>

        {/* Diritti GDPR */}
        <section className="bg-surface border border-subtle rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="font-display text-xl font-medium mb-4">I tuoi diritti (GDPR)</h2>
          <p className="text-sm text-secondary-fg leading-relaxed mb-3">
            Oltre alla cancellazione, hai diritto di:
          </p>
          <ul className="space-y-2 text-sm text-secondary-fg leading-relaxed list-disc pl-5">
            <li>Accedere ai tuoi dati</li>
            <li>Ottenere una copia esportabile (Impostazioni → Report famiglia PDF)</li>
            <li>Correggere i dati errati (Impostazioni → Profilo)</li>
            <li>Opporti al trattamento</li>
            <li>Presentare reclamo al Garante Privacy (garanteprivacy.it)</li>
          </ul>
          <p className="text-sm text-secondary-fg leading-relaxed mt-4">
            Titolare del trattamento: Family Brain · Contatto: <a href="mailto:info@familybrain.it" className="text-brand hover:underline">info@familybrain.it</a>
          </p>
        </section>

        <div className="mt-10 text-center text-xs text-tertiary-fg">
          <a href="/" data-testid="delete-account-back-home" className="hover:text-primary-fg">← Torna alla home</a>
          {" · "}
          <a href="/privacy" className="hover:text-primary-fg">Privacy Policy</a>
          {" · "}
          <a href="/terms" className="hover:text-primary-fg">Termini di Servizio</a>
        </div>
      </main>
    </div>
  );
}
